import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { message } from 'antd'
import { useToken } from 'app/hooks'
import API from 'utils/api'
import { useTeacherStudent } from 'app/gomsacademy/hooks/TeacherStudentList'
import { useGetMyInfo } from 'app/gomsacademy/hooks/GetMyInfo'

type RecipientType = 'parent' | 'student'

type MessageType = 'absence' | 'report' | 'attendance'

type TemplateItem = {
	templateCode: string
	templateName: string
	templateContent?: string
}

const Container = styled.div`
	width: 100%;
`

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`

const Card = styled.div`
	border: 1px solid #f6f1f1;
	border-radius: 12px;
	background: #fff;
	padding: 16px;
`

const CardTitle = styled.div`
	font-size: 16px;
	font-weight: 700;
	margin-bottom: 12px;
	color: #111;
`

const Controls = styled.div`
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 12px;
`

const Select = styled.select`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	min-width: 160px;
`

const Input = styled.input`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	flex: 1;
	min-width: 180px;
`

const ParamList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const ParamRow = styled.div`
	display: grid;
	grid-template-columns: 1fr auto;
	gap: 8px;
	align-items: center;
`

const ParamInput = styled.input`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	width: 100%;
`

const ParamActions = styled.div`
	display: flex;
	gap: 8px;
`

const List = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 8px;
	overflow: hidden;
	max-height: 420px;
	overflow-y: auto;
`

const ListRow = styled.label`
	display: flex;
	gap: 10px;
	align-items: center;
	padding: 10px 12px;
	border-bottom: 1px solid #f5f5f5;
	cursor: pointer;
	user-select: none;

	&:hover {
		background: #fafafa;
	}

	&:last-child {
		border-bottom: none;
	}
`

const Name = styled.span`
	font-weight: 600;
	color: #111;
`

const Meta = styled.span`
	color: #666;
	font-size: 12px;
`

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 12px;
	flex-wrap: wrap;
`

const Button = styled.button`
	padding: 10px 14px;
	border-radius: 10px;
	border: 1px solid #f78b85;
	background: #f78b85;
	color: #fff;
	font-weight: 700;
	cursor: pointer;

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`

const SecondaryButton = styled.button`
	padding: 10px 14px;
	border-radius: 10px;
	border: 1px solid #e4e1e1;
	background: #fff;
	color: #111;
	font-weight: 700;
	cursor: pointer;
`

const ResultTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #f6f1f1;
	margin-top: 16px;

	th {
		padding: 10px;
		background-color: #fff5f5;
		border: 1px solid #f6f1f1;
		text-align: left;
		font-weight: 700;
		color: #333;
		font-size: 13px;
	}
	
	td {
		padding: 10px;
		border: 1px solid #f6f1f1;
		color: #111;
		font-size: 13px;
	}
`

const PreviewBox = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 10px;
	background: #fafafa;
	padding: 12px;
	white-space: pre-wrap;
	font-size: 13px;
	line-height: 1.5;
	color: #111;
`

const PreviewMeta = styled.div`
	margin-top: 8px;
	font-size: 12px;
	color: #666;
`

const Badge = styled.span<{ ok: boolean }>`
	padding: 4px 8px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 700;
	background-color: ${p => (p.ok ? '#e6f7e6' : '#ffe6e6')};
	color: ${p => (p.ok ? '#52c41a' : '#ff4d4f')};
`

export const GroupMessageSend = () => {
	const { data: token } = useToken()
	const { TeacherStudentData: studentList } = useTeacherStudent(3)
	const { myInfo } = useGetMyInfo()

	const [recipientType, setRecipientType] = useState<RecipientType>('parent')
	const [messageType, setMessageType] = useState<MessageType>('absence')

	const [templateCode, setTemplateCode] = useState('')
	const [templateSearch, setTemplateSearch] = useState('')
	const [templates, setTemplates] = useState<TemplateItem[]>([])
	const [loadingTemplates, setLoadingTemplates] = useState(false)

	const [studentSearch, setStudentSearch] = useState('')
	const [selectedIds, setSelectedIds] = useState<number[]>([])

	const [templateParams, setTemplateParams] = useState<string[]>(['#{학원이름}', '#{학생이름}'])
	const [lastAutoTemplateParams, setLastAutoTemplateParams] = useState<string[]>([
		'#{학원이름}',
		'#{학생이름}',
	])

	const [sending, setSending] = useState(false)
	const [sendResult, setSendResult] = useState<any>(null)

	const selectedTemplate = useMemo(() => {
		if (!templateCode) return undefined
		return templates.find(t => t.templateCode === templateCode)
	}, [templates, templateCode])

	const previewStudent = useMemo(() => {
		if (!selectedIds.length) return undefined
		const firstId = selectedIds[0]
		const list = Array.isArray(studentList) ? studentList : []
		return list.find((s: any) => Number(s?.id) === Number(firstId))
	}, [selectedIds, studentList])

	const detectedPlaceholders = useMemo(() => {
		const content = selectedTemplate?.templateContent || ''
		const matches = content.match(/#\{[^}]+\}/g) || []
		// preserve order but dedupe
		const seen = new Set<string>()
		const unique: string[] = []
		for (const m of matches) {
			if (!seen.has(m)) {
				seen.add(m)
				unique.push(m)
			}
		}
		return unique
	}, [selectedTemplate])

	const resolvedTemplateParamValues = useMemo(() => {
		const academyName = String(myInfo?.branchName || myInfo?.deptName || '')
		const studentName = String(previewStudent?.name || '')
		return templateParams.map(p => {
			const raw = String(p || '').trim()
			if (raw === '#{학원이름}' || raw === '#{학원명}') return academyName
			if (raw === '#{학생이름}' || raw === '#{이름}') return studentName
			return raw
		})
	}, [templateParams, myInfo, previewStudent])

	const previewMessageText = useMemo(() => {
		const content = selectedTemplate?.templateContent || ''
		if (!content) return ''

		// If template content includes placeholders, replace by detected order with current param values.
		if (detectedPlaceholders.length === 0) return content

		let out = content
		detectedPlaceholders.forEach((ph, idx) => {
			const replacement = resolvedTemplateParamValues[idx]
			if (replacement == null || replacement === '') return
			out = out.split(ph).join(replacement)
		})
		return out
	}, [selectedTemplate, detectedPlaceholders, resolvedTemplateParamValues])

	const filteredStudents = useMemo(() => {
		const list = Array.isArray(studentList) ? studentList : []
		const keyword = studentSearch.trim().toLowerCase()
		if (!keyword) return list
		return list.filter((s: any) => {
			const name = String(s?.name || '').toLowerCase()
			const phone = String(s?.phone || '')
			const phone2 = String(s?.phone2 || '')
			return name.includes(keyword) || phone.includes(keyword) || phone2.includes(keyword)
		})
	}, [studentList, studentSearch])

	const filteredTemplates = useMemo(() => {
		const keyword = templateSearch.trim().toLowerCase()
		if (!keyword) return templates
		return templates.filter(
			t =>
				String(t.templateCode).toLowerCase().includes(keyword) ||
				String(t.templateName).toLowerCase().includes(keyword)
		)
	}, [templates, templateSearch])

	const selectedCount = selectedIds.length

	const toggleAllFiltered = () => {
		const ids = filteredStudents.map((s: any) => Number(s.id)).filter((v: any) => !Number.isNaN(v))
		const allSelected = ids.length > 0 && ids.every(id => selectedIds.includes(id))
		setSelectedIds(
			allSelected
				? selectedIds.filter(id => !ids.includes(id))
				: Array.from(new Set([...selectedIds, ...ids]))
		)
	}

	const toggleOne = (id: number) => {
		setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
	}

	const fetchTemplates = async () => {
		if (!token) return
		setLoadingTemplates(true)
		try {
			const params = new URLSearchParams()
			params.append('pageNum', '1')
			params.append('pageSize', '200')
			params.append('templateStatus', 'Y')
			const res: any = await API.get(`/message/templates?${params.toString()}`, token + '', {})

			if (res?.code !== 0) {
				message.error(res?.message || '템플릿 조회에 실패했습니다.')
				return
			}

			const result = res?.result
			const list = result?.list || result?.result?.list || []
			const normalized: TemplateItem[] = (Array.isArray(list) ? list : []).map((t: any) => ({
				templateCode: t.templateCode,
				templateName: t.templateName,
				templateContent: t.templateContent,
			}))
			setTemplates(normalized.filter(t => t.templateCode && t.templateName))
		} catch (e: any) {
			message.error(e?.message || '템플릿 조회 중 오류가 발생했습니다.')
		} finally {
			setLoadingTemplates(false)
		}
	}

	useEffect(() => {
		fetchTemplates()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token])

	useEffect(() => {
		// When template changes, update param placeholders to match detected placeholders.
		if (!templateCode) return
		if (!selectedTemplate?.templateContent) return

		const autoParams =
			detectedPlaceholders.length > 0 ? detectedPlaceholders : ['#{학원이름}', '#{학생이름}']

		const normalizedCurrent = templateParams.map(p => p.trim()).filter(Boolean)
		const normalizedLastAuto = lastAutoTemplateParams.map(p => p.trim()).filter(Boolean)
		const isEmpty = normalizedCurrent.length === 0
		const isUneditedSinceAuto =
			JSON.stringify(normalizedCurrent) === JSON.stringify(normalizedLastAuto)

		// Only auto-update if user hasn't edited since last auto-fill (or list is empty)
		if (isEmpty || isUneditedSinceAuto) {
			setTemplateParams(autoParams)
			setLastAutoTemplateParams(autoParams)
		} else {
			// still update the "last auto" marker so next switch works predictably
			setLastAutoTemplateParams(autoParams)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateCode, selectedTemplate?.templateContent])

	const updateTemplateParam = (index: number, value: string) => {
		setTemplateParams(prev => prev.map((p, i) => (i === index ? value : p)))
	}

	const addTemplateParam = () => {
		setTemplateParams(prev => [...prev, ''])
	}

	const removeTemplateParam = (index: number) => {
		setTemplateParams(prev => prev.filter((_, i) => i !== index))
	}

	const moveTemplateParam = (from: number, to: number) => {
		setTemplateParams(prev => {
			if (to < 0 || to >= prev.length) return prev
			const next = [...prev]
			const [item] = next.splice(from, 1)
			next.splice(to, 0, item)
			return next
		})
	}

	const handleSend = async () => {
		if (!token) return
		if (selectedIds.length === 0) {
			message.warning('수신자를 선택해 주세요.')
			return
		}
		if (!templateCode) {
			message.warning('템플릿을 선택해 주세요.')
			return
		}

		const templateParam = templateParams.map(s => s.trim()).filter(Boolean)

		const body: any = {
			studentIds: selectedIds,
			templateCode,
			recipientType,
			type: messageType,
			customParams: { templateParam },
		}

		if (!window.confirm(`선택한 ${selectedIds.length}명에게 메시지를 전송할까요?`)) return

		setSending(true)
		setSendResult(null)
		try {
			const res: any = await API.post('/message/send/group', token + '', body)
			if (res?.result?.success) {
				message.success('전송 요청이 완료되었습니다.')
				setSendResult(res.result)
			} else {
				message.error(res?.message || res?.result?.message || '전송에 실패했습니다.')
				setSendResult(res?.result)
			}
		} catch (e: any) {
			message.error(e?.message || '전송 중 오류가 발생했습니다.')
		} finally {
			setSending(false)
		}
	}

	return (
		<Container>
			<Row>
				<Card>
					<CardTitle>수신자 선택</CardTitle>
					<Controls>
						<Input
							value={studentSearch}
							onChange={e => setStudentSearch(e.target.value)}
							placeholder="이름/전화번호 검색"
						/>
						<SecondaryButton onClick={toggleAllFiltered}>필터 전체 선택/해제</SecondaryButton>
					</Controls>
					<List>
						{filteredStudents.map((s: any) => {
							const id = Number(s.id)
							const checked = selectedIds.includes(id)
							const parentTel = s?.phone2 ? String(s.phone2) : ''
							const studentTel = s?.phone ? String(s.phone) : ''
							return (
								<ListRow key={id}>
									<input type="checkbox" checked={checked} onChange={() => toggleOne(id)} />
									<div>
										<div>
											<Name>{s?.name}</Name>
										</div>
										<Meta>
											학부모: {parentTel || '-'} / 학생: {studentTel || '-'}
										</Meta>
									</div>
								</ListRow>
							)
						})}
					</List>
					<Footer>
						<div>선택: {selectedCount}명</div>
						<SecondaryButton onClick={() => setSelectedIds([])}>선택 초기화</SecondaryButton>
					</Footer>
				</Card>

				<Card>
					<CardTitle>전송 설정</CardTitle>
					<Controls>
						<Select
							value={recipientType}
							onChange={e => setRecipientType(e.target.value as RecipientType)}>
							<option value="parent">학부모(보호자) 번호</option>
							<option value="student">학생 번호</option>
						</Select>
						<Select
							value={messageType}
							onChange={e => setMessageType(e.target.value as MessageType)}>
							<option value="absence">결시</option>
							<option value="report">일지발송</option>
							<option value="attendance">등원</option>
						</Select>
					</Controls>

					<Controls>
						<Input
							value={templateSearch}
							onChange={e => setTemplateSearch(e.target.value)}
							placeholder="템플릿 검색(코드/이름)"
						/>
						<Select value={templateCode} onChange={e => setTemplateCode(e.target.value)}>
							<option value="">템플릿 선택</option>
							{filteredTemplates.map(t => (
								<option key={t.templateCode} value={t.templateCode}>
									{t.templateName} ({t.templateCode})
								</option>
							))}
						</Select>
						<SecondaryButton onClick={fetchTemplates} disabled={loadingTemplates}>
							{loadingTemplates ? '불러오는 중...' : '템플릿 새로고침'}
						</SecondaryButton>
					</Controls>

					<div style={{ marginTop: 8, marginBottom: 8, fontWeight: 700 }}>
						템플릿 파라미터(줄바꿈 = 항목)
						{detectedPlaceholders.length > 0 ? (
							<span style={{ marginLeft: 8, fontWeight: 600, color: '#666', fontSize: 12 }}>
								감지됨: {detectedPlaceholders.join(', ')}
							</span>
						) : null}
					</div>
					<ParamList>
						{templateParams.length === 0 ? (
							<div style={{ color: '#666', fontSize: 13 }}>
								파라미터가 없습니다. 필요 시 추가해 주세요.
							</div>
						) : null}
						{templateParams.map((p, idx) => (
							<ParamRow key={idx}>
								<ParamInput
									value={p}
									onChange={e => updateTemplateParam(idx, e.target.value)}
									placeholder={idx === 0 ? '#{학원이름}' : idx === 1 ? '#{학생이름}' : ''}
								/>
								<ParamActions>
									<SecondaryButton
										type="button"
										onClick={() => moveTemplateParam(idx, idx - 1)}
										disabled={idx === 0}>
										위
									</SecondaryButton>
									<SecondaryButton
										type="button"
										onClick={() => moveTemplateParam(idx, idx + 1)}
										disabled={idx === templateParams.length - 1}>
										아래
									</SecondaryButton>
									<SecondaryButton type="button" onClick={() => removeTemplateParam(idx)}>
										삭제
									</SecondaryButton>
								</ParamActions>
							</ParamRow>
						))}
						<div>
							<SecondaryButton type="button" onClick={addTemplateParam}>
								+ 파라미터 추가
							</SecondaryButton>
						</div>
					</ParamList>

					<div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>
						메시지 내용 미리보기
					</div>
					<PreviewBox>
						{!selectedTemplate?.templateContent
							? '템플릿을 선택하면 내용 미리보기가 표시됩니다.'
							: previewMessageText || selectedTemplate.templateContent}
					</PreviewBox>
					<PreviewMeta>
						미리보기 대상:{' '}
						{previewStudent?.name
							? `${previewStudent.name} (선택 ${selectedIds.length}명 중 1명)`
							: `선택 ${selectedIds.length}명`}
						{myInfo?.branchName ? ` / 학원: ${myInfo.branchName}` : ''}
					</PreviewMeta>

					<Footer>
						<div />
						<Button onClick={handleSend} disabled={sending}>
							{sending ? '전송 중...' : '단체 전송'}
						</Button>
					</Footer>
				</Card>
			</Row>

			{sendResult?.results && (
				<ResultTable>
					<thead>
						<tr>
							<th>이름</th>
							<th>전화번호</th>
							<th>결과</th>
						</tr>
					</thead>
					<tbody>
						{sendResult.results.map((r: any, idx: number) => (
							<tr key={idx}>
								<td>{r.name}</td>
								<td>{r.phone || '-'}</td>
								<td>
									<Badge ok={!!r.success}>{r.success ? '성공' : '실패'}</Badge>
									{r.reason ? ` (${r.reason})` : ''}
								</td>
							</tr>
						))}
					</tbody>
				</ResultTable>
			)}
		</Container>
	)
}

export default React.memo(GroupMessageSend)
